import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Checkbox,
  MenuItem,
  Alert,
  CircularProgress
} from "@mui/material";

import { ALL_PAGES } from "../../auth/roles";

const emptyPermissionsFor = (pages) =>
  pages.reduce((map, page) => {
    map[page.page_key] = {
      access_level: page.access_level || "write",
      data_scope: page.data_scope || "all"
    };
    return map;
  }, {});

function RoleDialog({
  open,
  role,
  isSaving = false,
  onClose,
  onSave
}) {
  const [roleName, setRoleName] = useState("");
  // { [page_key]: { access_level, data_scope } } - presence of a key means the page is enabled.
  const [permissions, setPermissions] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setRoleName(role ? role.role_name.replace(/_/g, " ") : "");
    setPermissions(role ? emptyPermissionsFor(role.pages) : {});
    setError("");
  }, [open, role]);

  const togglePage = (pageKey, checked) => {
    setPermissions((prev) => {
      const next = { ...prev };
      if (checked) {
        next[pageKey] = { access_level: "write", data_scope: "all" };
      } else {
        delete next[pageKey];
      }
      return next;
    });
  };

  const updatePagePermission = (pageKey, field, value) => {
    setPermissions((prev) => ({
      ...prev,
      [pageKey]: { ...prev[pageKey], [field]: value }
    }));
  };

  const handleSave = () => {
    if (!roleName.trim()) {
      setError("Role name is required");
      return;
    }

    const pages = Object.entries(permissions).map(([page_key, perm]) => ({
      page_key,
      access_level: perm.access_level,
      data_scope: perm.data_scope
    }));

    onSave({
      role_name: roleName.trim(),
      pages
    });
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {role ? "Update Role" : "Add Role"}
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
          Role Name
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="e.g. HR Admin"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
        />

        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
          Pages this role can access
        </Typography>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto minmax(140px, 1.4fr) 160px 160px",
              alignItems: "center",
              columnGap: 1.5,
              px: 2,
              py: 1,
              bgcolor: "background.default",
              borderBottom: "1px solid",
              borderColor: "divider"
            }}
          >
            <Box sx={{ width: 30 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Page
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Data Scope
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Access
            </Typography>
          </Box>

          {ALL_PAGES.map((page, index) => {
            const perm = permissions[page.key];
            const isChecked = Boolean(perm);

            return (
              <Box
                key={page.key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto minmax(140px, 1.4fr) 160px 160px",
                  alignItems: "center",
                  columnGap: 1.5,
                  px: 2,
                  py: 1.1,
                  borderTop: index === 0 ? "none" : "1px solid",
                  borderColor: "divider",
                  bgcolor: isChecked ? "background.paper" : "background.default",
                  "&:hover": { bgcolor: "action.hover" }
                }}
              >
                <Checkbox
                  size="small"
                  checked={isChecked}
                  onChange={(e) => togglePage(page.key, e.target.checked)}
                  sx={{ p: 0.5 }}
                />

                <Typography
                  fontSize={14}
                  sx={{ fontWeight: 600, color: isChecked ? "text.primary" : "text.disabled" }}
                >
                  {page.label}
                </Typography>

                <TextField
                  select
                  size="small"
                  fullWidth
                  disabled={!isChecked}
                  value={perm?.data_scope || "all"}
                  onChange={(e) => updatePagePermission(page.key, "data_scope", e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: isChecked ? "background.paper" : "background.default"
                    }
                  }}
                >
                  <MenuItem value="own">Own data</MenuItem>
                  <MenuItem value="all">All data</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  fullWidth
                  disabled={!isChecked}
                  value={perm?.access_level || "write"}
                  onChange={(e) => updatePagePermission(page.key, "access_level", e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      bgcolor: isChecked ? "background.paper" : "background.default"
                    }
                  }}
                >
                  <MenuItem value="read">Read only</MenuItem>
                  <MenuItem value="write">Write access</MenuItem>
                </TextField>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, bgcolor: "background.default" }}>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RoleDialog;
